/* eslint-disable no-param-reassign */
import React, {
  useCallback,
  useState,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react'

import PropTypes from 'prop-types'

import get from 'lodash/get'
import map from 'lodash/map'

import {
  RadioGroup as MuiRadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Radio as MuiRadio
} from '@material-ui/core'

import { FieldProvider, useField } from '@galo-monitor/forms'

import { Container, Label } from './styles'

const RadioGroup = forwardRef((props, externalRef) => {
  const {
    options,
    defaultValue,
    variant,
    style,
    notSetFieldRef,
    label: externalLabel,
    labelSize,
    clearable
  } = props

  const {
    fieldRef,
    handleChangeNative,
    resetField,
    externalOnChange,
    validateField
  } = useField()

  const inputRef = useRef(null)

  const [value, setValue] = useState(defaultValue)
  const [mounted, setMounted] = useState(false)

  const handleChange = useCallback(
    (evt) => {
      const newValue = evt?.target?.value ?? ''

      setValue(old => {
        if (clearable && old === newValue) {
          return ''
        }
        return newValue
      })
    },
    [clearable]
  )

  useEffect(() => {
    if (mounted && !notSetFieldRef) {
      handleChangeNative(value)
    } else {
      setMounted(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, handleChangeNative])

  useImperativeHandle(fieldRef, () => ({
    ...(!notSetFieldRef ? inputRef.current : {}),
    ...(fieldRef.current || {}),
    ...(!notSetFieldRef ? {
      defaultValue,
      setValue,
      resetField,
      externalOnChange,
      validateField
    } : {})
  }))

  useImperativeHandle(externalRef, () => ({
    ...(inputRef.current),
    defaultValue,
    setValue,
    resetField
  }))

  return (
    <Container style={ { ...style } }>
      {externalLabel !== '' && (
        <Label size={ labelSize }>{ externalLabel }</Label>
      )}
      <MuiRadioGroup
        defaultValue={ defaultValue }
        value={ value }
        row={ variant === 'row' }
        onChange={ handleChange }
      >
        {
          map(options, ({ label, value: optionValue }) => (
            <MuiFormControlLabel
              key={ optionValue }
              value={ optionValue }
              control={ <MuiRadio color="primary" /> }
              label={ label }
            />
          ))
        }
      </MuiRadioGroup>
    </Container>
  )
})

RadioGroup.propTypes = {
  options: PropTypes.array.isRequired,
  defaultValue: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool,
    PropTypes.string
  ]),
  variant: PropTypes.oneOf(['row', 'column']),
  style: PropTypes.object,
  notSetFieldRef: PropTypes.bool,
  label: PropTypes.string,
  labelSize: PropTypes.string,
  clearable: PropTypes.bool
}

RadioGroup.defaultProps = {
  defaultValue: undefined,
  variant: 'column',
  style: {},
  notSetFieldRef: false,
  label: '',
  labelSize: 'normal',
  clearable: false
}

const Field = forwardRef((props, fieldRef) => (
  <FieldProvider
    ref={ fieldRef }
    { ...props }
    registerFieldOptions={ {
      path: 'value',
      clearValue(ref) {
        if (ref) {
          ref.value = ''
          ref.clear()
        }
      },
      getValue(ref) {
        return get(ref, 'value')
      }
    } }
    FieldComponent={ RadioGroup }
  />
))


Field.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  defaultValue: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool,
    PropTypes.string
  ]),
  variant: PropTypes.oneOf(['row', 'column']),
  style: PropTypes.object,
  notSetFieldRef: PropTypes.bool,
  clearable: PropTypes.bool
}

Field.defaultProps = {
  onChange: (param) => (param),
  defaultValue: undefined,
  variant: 'column',
  style: {},
  notSetFieldRef: false,
  clearable: false
}

export default Field
